import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { FicheEntreprise } from '../components/FicheEntreprise';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  Building2, Users, Target, Phone, Mail, Plus, Search, TrendingUp,
  BarChart3, Calendar, CheckCircle2, Clock, AlertCircle, ArrowRight,
  Briefcase, Globe, MapPin, Euro, Sparkles, FileText, Upload, Download,
  PhoneCall, PhoneOff, MessageSquare, Bot, Timer, Rocket
} from 'lucide-react';
import CRMVenteLogicielPage from './CRMVenteLogicielPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs pour le pipeline
const ETAPE_COLORS = {
  prospection: 'bg-gray-500',
  qualification: 'bg-blue-500',
  proposition: 'bg-yellow-500',
  negociation: 'bg-orange-500',
  closing: 'bg-purple-500',
  gagne: 'bg-green-500',
  perdu: 'bg-red-500'
};

const ETAPE_LABELS = {
  prospection: 'Prospection',
  qualification: 'Qualification',
  proposition: 'Proposition',
  negociation: 'Négociation',
  closing: 'Closing',
  gagne: 'Gagné',
  perdu: 'Perdu'
};

export default function CRMHubPage() {
  const { activeSociete } = useWorkspace();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Lists
  const [contacts, setContacts] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [opportunites, setOpportunites] = useState([]);
  
  // Search
  const [searchContact, setSearchContact] = useState('');
  const [searchEntreprise, setSearchEntreprise] = useState('');
  
  // Dialogs
  const [showNewContact, setShowNewContact] = useState(false);
  const [showNewEntreprise, setShowNewEntreprise] = useState(false);
  const [showNewOpportunite, setShowNewOpportunite] = useState(false);
  const [showAnalyseMarche, setShowAnalyseMarche] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  
  // Click-to-Call States
  const [showCallModal, setShowCallModal] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callStartTime, setCallStartTime] = useState(null);
  const [callNotes, setCallNotes] = useState('');
  const [callResult, setCallResult] = useState('positif');
  const [savingCall, setSavingCall] = useState(false);
  const [analyzingCall, setAnalyzingCall] = useState(false);
  const [appels, setAppels] = useState([]);
  const [showAppelsTab, setShowAppelsTab] = useState(false);
  
  // Forms
  const [newContact, setNewContact] = useState({
    prenom: '', nom: '', email: '', telephone: '', poste: '',
    entreprise_nom: '', niveau_interet: 'tiede', est_decideur: false
  });
  
  const [newEntreprise, setNewEntreprise] = useState({
    nom: '', siret: '', secteur_activite: 'agriculture', sous_secteur: '',
    adresse: '', code_postal: '', ville: '', telephone: '', email: '',
    type_client: 'prospect', potentiel: 'moyen', ca_estime: 0
  });
  
  const [newOpportunite, setNewOpportunite] = useState({
    nom: '', entreprise_id: '', entreprise_nom: '', montant_estime: 0,
    probabilite: 10, etape: 'prospection', date_cloture_prevue: ''
  });
  
  const [analyseMarche, setAnalyseMarche] = useState({
    produit: '', quantite: '', unite: 'kg', type_vente: 'B2B', region: ''
  });
  const [analyseResult, setAnalyseResult] = useState(null);
  const [loadingAnalyse, setLoadingAnalyse] = useState(false);
  
  const token = localStorage.getItem('token');
  // Utiliser activeSociete du WorkspaceContext en priorité
  const societeId = activeSociete?.id || localStorage.getItem('activeSocieteId') || localStorage.getItem('selectedSocieteId');
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Societe-Id': societeId || ''
  };
  
  // Fetch Dashboard
  const fetchDashboard = useCallback(async () => {
    if (!societeId) return;
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/dashboard`, { headers });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Erreur dashboard:', err);
    }
  }, [societeId]);
  
  // Fetch Contacts
  const fetchContacts = useCallback(async () => {
    if (!societeId) return;
    try {
      const url = searchContact 
        ? `${API_URL}/api/crm-hub/contacts?search=${encodeURIComponent(searchContact)}`
        : `${API_URL}/api/crm-hub/contacts`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
      }
    } catch (err) {
      console.error('Erreur contacts:', err);
    }
  }, [searchContact, societeId]);
  
  // Fetch Entreprises
  const fetchEntreprises = useCallback(async () => {
    if (!societeId) return;
    try {
      const url = searchEntreprise 
        ? `${API_URL}/api/crm-hub/entreprises?search=${encodeURIComponent(searchEntreprise)}`
        : `${API_URL}/api/crm-hub/entreprises`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setEntreprises(data.entreprises || []);
      }
    } catch (err) {
      console.error('Erreur entreprises:', err);
    }
  }, [searchEntreprise, societeId]);
  
  // Fetch Opportunités
  const fetchOpportunites = useCallback(async () => {
    if (!societeId) return;
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/opportunites`, { headers });
      if (res.ok) {
        const data = await res.json();
        setOpportunites(data.opportunites || []);
      }
    } catch (err) {
      console.error('Erreur opportunités:', err);
    }
  }, [societeId]);
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchContacts(), fetchEntreprises(), fetchOpportunites()]);
      setLoading(false);
    };
    if (societeId) {
      loadData();
    } else {
      // Pas de société sélectionnée — sortir du loading pour afficher un message
      setLoading(false);
    }
  }, [societeId, fetchDashboard, fetchContacts, fetchEntreprises, fetchOpportunites]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchContacts();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchContact]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEntreprises();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchEntreprise]);
  
  // Create Contact
  const handleCreateContact = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/contacts`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newContact)
      });
      if (res.ok) {
        toast.success('Contact créé !');
        setShowNewContact(false);
        setNewContact({ prenom: '', nom: '', email: '', telephone: '', poste: '', entreprise_nom: '', niveau_interet: 'tiede', est_decideur: false });
        fetchContacts();
        fetchDashboard();
      } else {
        toast.error('Erreur création contact');
      }
    } catch (err) {
      toast.error('Erreur réseau');
    }
  };
  
  // Create Entreprise
  const handleCreateEntreprise = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/entreprises`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newEntreprise)
      });
      if (res.ok) {
        toast.success('Entreprise créée !');
        setShowNewEntreprise(false);
        setNewEntreprise({ nom: '', siret: '', secteur_activite: 'agriculture', sous_secteur: '', adresse: '', code_postal: '', ville: '', telephone: '', email: '', type_client: 'prospect', potentiel: 'moyen', ca_estime: 0 });
        fetchEntreprises();
        fetchDashboard();
      } else {
        toast.error('Erreur création entreprise');
      }
    } catch (err) {
      toast.error('Erreur réseau');
    }
  };
  
  // Create Opportunité
  const handleCreateOpportunite = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/opportunites`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newOpportunite)
      });
      if (res.ok) {
        toast.success('Opportunité créée !');
        setShowNewOpportunite(false);
        setNewOpportunite({ nom: '', entreprise_id: '', entreprise_nom: '', montant_estime: 0, probabilite: 10, etape: 'prospection', date_cloture_prevue: '' });
        fetchOpportunites();
        fetchDashboard();
      } else {
        toast.error('Erreur création opportunité');
      }
    } catch (err) {
      toast.error('Erreur réseau');
    }
  };
  
  // Analyse Marché IA
  const handleAnalyseMarche = async () => {
    setLoadingAnalyse(true);
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/analyse-marche`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...analyseMarche,
          quantite: analyseMarche.quantite ? parseFloat(analyseMarche.quantite) : null
        })
      });
      if (res.ok) {
        const data = await res.json();
        setAnalyseResult(data);
        toast.success('Analyse terminée !');
      } else {
        toast.error('Erreur analyse');
      }
    } catch (err) {
      toast.error('Erreur réseau');
    }
    setLoadingAnalyse(false);
  };
  
  // Update Opportunité étape
  const handleUpdateOpportuniteEtape = async (oppId, newEtape) => {
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/opportunites/${oppId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ etape: newEtape })
      });
      if (res.ok) {
        toast.success('Étape mise à jour !');
        fetchOpportunites();
        fetchDashboard();
      }
    } catch (err) {
      toast.error('Erreur');
    }
  };

  // ============ CLICK-TO-CALL FUNCTIONS ============
  
  // Initier un appel
  const handleStartCall = (contact) => {
    setActiveCall(contact);
    setCallStartTime(new Date());
    setCallNotes('');
    setCallResult('positif');
    setShowCallModal(true);
    
    // Ouvrir le lien tel: pour déclencher l'appel
    const phone = contact.mobile || contact.telephone;
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };
  
  // Sauvegarder l'appel
  const handleSaveCall = async () => {
    if (!activeCall) return;
    
    setSavingCall(true);
    const duration = callStartTime ? Math.floor((new Date() - callStartTime) / 1000) : 0;
    
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/appels`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          contact_id: activeCall.id,
          contact_nom: `${activeCall.prenom || ''} ${activeCall.nom || ''}`.trim(),
          entreprise_id: activeCall.entreprise_id,
          entreprise_nom: activeCall.entreprise_nom,
          direction: 'sortant',
          numero_telephone: activeCall.mobile || activeCall.telephone,
          duree_secondes: duration,
          statut: 'termine',
          notes: callNotes,
          resultat: callResult
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Appel enregistré !');
        
        // Proposer l'analyse IA si des notes existent
        if (callNotes.trim().length > 20) {
          toast.info('Analyse IA des notes en cours...');
          await handleAnalyzeCall(data.appel.id);
        }
        
        setShowCallModal(false);
        setActiveCall(null);
        fetchAppels();
      } else {
        toast.error('Erreur enregistrement appel');
      }
    } catch (err) {
      toast.error('Erreur réseau');
    }
    setSavingCall(false);
  };
  
  // Analyser un appel avec l'IA
  const handleAnalyzeCall = async (appelId) => {
    setAnalyzingCall(true);
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/appels/${appelId}/analyser`, {
        method: 'POST',
        headers
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.taches_creees?.length > 0) {
          toast.success(`Analyse terminée ! ${data.taches_creees.length} tâche(s) créée(s)`);
        } else {
          toast.success('Analyse terminée !');
        }
        fetchAppels();
      }
    } catch (err) {
      console.error('Erreur analyse:', err);
    }
    setAnalyzingCall(false);
  };
  
  // Charger les appels
  const fetchAppels = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm-hub/appels?limit=50`, { headers });
      if (res.ok) {
        const data = await res.json();
        setAppels(data.appels || []);
      }
    } catch (err) {
      console.error('Erreur appels:', err);
    }
  }, []);
  
  // Charger les appels au démarrage
  useEffect(() => {
    fetchAppels();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!societeId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
        <div className="max-w-md text-center" data-testid="crm-no-societe">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-orange-400 opacity-60" />
          <h2 className="text-2xl font-bold mb-3">Sélectionnez une société</h2>
          <p className="text-slate-400 text-sm leading-relaxed mb-6">
            Le CRM est cloisonné par société. Choisissez l'ETA Sauvayre,
            Bureau Agricole, CASIA ou une autre depuis le sélecteur de
            société dans la barre latérale pour afficher ses contacts,
            entreprises et opportunités.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Building2 className="w-8 h-8 text-orange-500" />
            CRM Hub
          </h1>
          <p className="text-slate-400 mt-1">Gestion commerciale style HubSpot</p>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import HubSpot
          </Button>
          <Button onClick={() => setShowAnalyseMarche(true)} className="bg-gradient-to-r from-purple-600 to-pink-600">
            <Sparkles className="w-4 h-4 mr-2" />
            Analyse Marché IA
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 mb-6">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-orange-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="contacts" className="data-[state=active]:bg-orange-600">
            <Users className="w-4 h-4 mr-2" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="entreprises" className="data-[state=active]:bg-orange-600">
            <Building2 className="w-4 h-4 mr-2" />
            Entreprises
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="data-[state=active]:bg-orange-600">
            <Target className="w-4 h-4 mr-2" />
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="appels" className="data-[state=active]:bg-green-600">
            <PhoneCall className="w-4 h-4 mr-2" />
            Appels
          </TabsTrigger>
          <TabsTrigger value="vente-logiciel" className="data-[state=active]:bg-purple-600" data-testid="tab-vente-logiciel">
            <Rocket className="w-4 h-4 mr-2" />
            Tunnel de Vente
          </TabsTrigger>
        </TabsList>
        
        {/* DASHBOARD */}
        <TabsContent value="dashboard">
          {dashboard && (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Users className="w-8 h-8 text-blue-500" />
                      <span className="text-3xl font-bold">{dashboard.kpis.nb_contacts}</span>
                    </div>
                    <p className="text-slate-400 mt-2">Contacts</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Building2 className="w-8 h-8 text-green-500" />
                      <span className="text-3xl font-bold">{dashboard.kpis.nb_entreprises}</span>
                    </div>
                    <p className="text-slate-400 mt-2">Entreprises</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Target className="w-8 h-8 text-orange-500" />
                      <span className="text-3xl font-bold">{dashboard.kpis.nb_opportunites}</span>
                    </div>
                    <p className="text-slate-400 mt-2">Opportunités</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <Euro className="w-8 h-8 text-yellow-500" />
                      <span className="text-2xl font-bold">{dashboard.kpis.montant_en_cours.toLocaleString('fr-FR')}€</span>
                    </div>
                    <p className="text-slate-400 mt-2">Pipeline</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Pipeline Overview */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle>Pipeline de vente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {Object.entries(dashboard.pipeline_stats).map(([etape, count]) => (
                      <div key={etape} className="flex-shrink-0 text-center p-3 bg-slate-700 rounded-lg min-w-[100px]">
                        <div className={`w-3 h-3 rounded-full ${ETAPE_COLORS[etape]} mx-auto mb-2`}></div>
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-xs text-slate-400">{ETAPE_LABELS[etape]}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* CA Gagné */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-gradient-to-r from-green-900 to-green-800 border-green-700">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <CheckCircle2 className="w-12 h-12 text-green-400" />
                      <div>
                        <p className="text-4xl font-bold text-green-300">{dashboard.kpis.ca_gagne.toLocaleString('fr-FR')}€</p>
                        <p className="text-green-400">CA Gagné</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-r from-purple-900 to-purple-800 border-purple-700">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <TrendingUp className="w-12 h-12 text-purple-400" />
                      <div>
                        <p className="text-4xl font-bold text-purple-300">{dashboard.kpis.montant_pondere.toLocaleString('fr-FR')}€</p>
                        <p className="text-purple-400">CA Pondéré</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
        
        {/* CONTACTS */}
        <TabsContent value="contacts">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  className="pl-10 bg-slate-800 border-slate-700"
                  placeholder="Rechercher un contact..."
                  value={searchContact}
                  onChange={(e) => setSearchContact(e.target.value)}
                />
              </div>
              <Button onClick={() => setShowNewContact(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Contact
              </Button>
            </div>
            
            <div className="grid gap-4">
              {contacts.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-400">Aucun contact. Créez votre premier contact !</p>
                  </CardContent>
                </Card>
              ) : (
                contacts.map((contact) => (
                  <Card key={contact.id} className="bg-slate-800 border-slate-700 hover:border-orange-500 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center text-lg font-bold">
                            {contact.prenom?.[0]}{contact.nom?.[0]}
                          </div>
                          <div>
                            <h3 className="font-semibold">{contact.prenom} {contact.nom}</h3>
                            <p className="text-sm text-slate-400">{contact.poste || 'Sans poste'}</p>
                            {contact.entreprise_nom && (
                              <p className="text-xs text-orange-400">{contact.entreprise_nom}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="p-2 hover:bg-slate-700 rounded" title="Envoyer un email">
                              <Mail className="w-4 h-4" />
                            </a>
                          )}
                          {(contact.telephone || contact.mobile) && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="bg-green-600 hover:bg-green-700 border-green-500 text-white"
                              onClick={() => handleStartCall(contact)}
                              data-testid={`call-contact-${contact.id}`}
                            >
                              <PhoneCall className="w-4 h-4 mr-1" />
                              Appeler
                            </Button>
                          )}
                          <Badge className={
                            contact.niveau_interet === 'tres_chaud' ? 'bg-red-500' :
                            contact.niveau_interet === 'chaud' ? 'bg-orange-500' :
                            contact.niveau_interet === 'tiede' ? 'bg-yellow-500' : 'bg-blue-500'
                          }>
                            {contact.niveau_interet}
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* ENTREPRISES */}
        <TabsContent value="entreprises">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  className="pl-10 bg-slate-800 border-slate-700"
                  placeholder="Rechercher une entreprise..."
                  value={searchEntreprise}
                  onChange={(e) => setSearchEntreprise(e.target.value)}
                />
              </div>
              <Button onClick={() => setShowNewEntreprise(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Entreprise
              </Button>
            </div>
            
            <div className="grid gap-4">
              {entreprises.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-8 text-center">
                    <Building2 className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-400">Aucune entreprise. Créez votre première entreprise !</p>
                  </CardContent>
                </Card>
              ) : (
                entreprises.map((ent) => (
                  <Card 
                    key={ent.id} 
                    className="bg-slate-800 border-slate-700 hover:border-green-500 transition-colors cursor-pointer"
                    onClick={() => setSelectedEntreprise(ent)}
                    data-testid={`entreprise-card-${ent.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                            <Building2 className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{ent.nom}</h3>
                            <p className="text-sm text-slate-400">{ent.secteur_activite} {ent.sous_secteur && `• ${ent.sous_secteur}`}</p>
                            {ent.siret && (
                              <p className="text-xs text-slate-500 font-mono">SIRET: {ent.siret}</p>
                            )}
                            {ent.ville && (
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {ent.code_postal} {ent.ville}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-semibold text-green-400">{ent.ca_estime?.toLocaleString('fr-FR') || 0}€</p>
                            <p className="text-xs text-slate-400">CA estimé</p>
                          </div>
                          <Badge className={
                            ent.type_client === 'client' ? 'bg-green-500' :
                            ent.type_client === 'prospect' ? 'bg-blue-500' :
                            ent.type_client === 'partenaire' ? 'bg-purple-500' : 'bg-slate-500'
                          }>
                            {ent.type_client}
                          </Badge>
                          {ent.potentiel && (
                            <Badge className={
                              ent.potentiel === 'strategique' ? 'bg-red-500' :
                              ent.potentiel === 'fort' ? 'bg-orange-500' :
                              ent.potentiel === 'moyen' ? 'bg-yellow-500' : 'bg-gray-500'
                            }>
                              {ent.potentiel}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* PIPELINE */}
        <TabsContent value="pipeline">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Pipeline de vente</h2>
              <Button onClick={() => setShowNewOpportunite(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Opportunité
              </Button>
            </div>
            
            {/* Kanban-style Pipeline */}
            <div className="flex gap-4 overflow-x-auto pb-4">
              {['prospection', 'qualification', 'proposition', 'negociation', 'closing', 'gagne', 'perdu'].map((etape) => (
                <div key={etape} className="flex-shrink-0 w-72">
                  <div className={`p-3 rounded-t-lg ${ETAPE_COLORS[etape]}`}>
                    <h3 className="font-semibold text-center">{ETAPE_LABELS[etape]}</h3>
                    <p className="text-xs text-center opacity-75">
                      {opportunites.filter(o => o.etape === etape).length} opportunité(s)
                    </p>
                  </div>
                  <div className="bg-slate-800 rounded-b-lg p-2 min-h-[300px] space-y-2">
                    {opportunites.filter(o => o.etape === etape).map((opp) => (
                      <Card key={opp.id} className="bg-slate-700 border-slate-600 hover:border-orange-500 cursor-pointer">
                        <CardContent className="p-3">
                          <h4 className="font-medium text-sm">{opp.nom}</h4>
                          <p className="text-xs text-slate-400">{opp.entreprise_nom}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-green-400 font-semibold">{opp.montant_estime?.toLocaleString('fr-FR')}€</span>
                            <span className="text-xs text-slate-400">{opp.probabilite}%</span>
                          </div>
                          {etape !== 'gagne' && etape !== 'perdu' && (
                            <div className="mt-2 flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-xs h-6"
                                onClick={() => {
                                  const etapes = ['prospection', 'qualification', 'proposition', 'negociation', 'closing', 'gagne'];
                                  const currentIdx = etapes.indexOf(etape);
                                  if (currentIdx < etapes.length - 1) {
                                    handleUpdateOpportuniteEtape(opp.id, etapes[currentIdx + 1]);
                                  }
                                }}
                              >
                                <ArrowRight className="w-3 h-3 mr-1" />
                                Avancer
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* APPELS - Journal des appels */}
        <TabsContent value="appels">
          <div className="space-y-6">
            {/* Stats appels */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <PhoneCall className="w-8 h-8 text-green-500" />
                    <span className="text-3xl font-bold">{appels.length}</span>
                  </div>
                  <p className="text-slate-400 mt-2">Total appels</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <Timer className="w-8 h-8 text-blue-500" />
                    <span className="text-3xl font-bold">
                      {Math.round(appels.reduce((acc, a) => acc + (a.duree_secondes || 0), 0) / 60)}
                    </span>
                  </div>
                  <p className="text-slate-400 mt-2">Minutes totales</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                    <span className="text-3xl font-bold">
                      {appels.filter(a => a.resultat === 'positif').length}
                    </span>
                  </div>
                  <p className="text-slate-400 mt-2">Appels positifs</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <Bot className="w-8 h-8 text-purple-500" />
                    <span className="text-3xl font-bold">
                      {appels.filter(a => a.resume_ia).length}
                    </span>
                  </div>
                  <p className="text-slate-400 mt-2">Analysés par IA</p>
                </CardContent>
              </Card>
            </div>

            {/* Liste des appels */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PhoneCall className="w-5 h-5 text-green-500" />
                  Journal des appels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <div className="space-y-3">
                    {appels.length === 0 ? (
                      <div className="text-center py-12 text-slate-400">
                        <PhoneOff className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Aucun appel enregistré</p>
                        <p className="text-sm">Cliquez sur "Appeler" sur une fiche contact pour commencer</p>
                      </div>
                    ) : (
                      appels.map((appel) => (
                        <div key={appel.id} className="p-4 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${appel.direction === 'sortant' ? 'bg-green-600' : 'bg-blue-600'}`}>
                                {appel.direction === 'sortant' ? <PhoneCall className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                              </div>
                              <div>
                                <h4 className="font-semibold">{appel.contact_nom || 'Contact inconnu'}</h4>
                                <p className="text-sm text-slate-400">{appel.entreprise_nom}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {new Date(appel.date_appel).toLocaleString('fr-FR')} • {Math.round((appel.duree_secondes || 0) / 60)} min
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                appel.resultat === 'positif' ? 'bg-green-600' :
                                appel.resultat === 'rappeler' ? 'bg-yellow-600' :
                                appel.resultat === 'negatif' ? 'bg-red-600' : 'bg-slate-600'
                              }>
                                {appel.resultat || 'Non qualifié'}
                              </Badge>
                              {appel.resume_ia && (
                                <Badge className="bg-purple-600">
                                  <Bot className="w-3 h-3 mr-1" />
                                  IA
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          {appel.notes && (
                            <div className="mt-3 p-3 bg-slate-800 rounded text-sm text-slate-300">
                              <MessageSquare className="w-4 h-4 inline mr-2 text-slate-500" />
                              {appel.notes}
                            </div>
                          )}
                          
                          {appel.resume_ia && (
                            <div className="mt-3 p-3 bg-purple-900/30 rounded">
                              <p className="text-sm text-purple-300">
                                <Bot className="w-4 h-4 inline mr-2" />
                                <strong>Résumé IA:</strong> {appel.resume_ia}
                              </p>
                              {appel.actions_ia?.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs text-slate-400 mb-1">Actions suggérées:</p>
                                  <ul className="list-disc list-inside text-sm text-purple-200">
                                    {appel.actions_ia.map((action, i) => (
                                      <li key={i}>{action}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {!appel.resume_ia && appel.notes && appel.notes.length > 20 && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="mt-3 bg-purple-600/20 border-purple-500"
                              onClick={() => handleAnalyzeCall(appel.id)}
                              disabled={analyzingCall}
                            >
                              <Bot className="w-4 h-4 mr-2" />
                              {analyzingCall ? 'Analyse en cours...' : 'Analyser avec IA'}
                            </Button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TUNNEL DE VENTE LOGICIEL - fusionné depuis /crm-vente-logiciel */}
        <TabsContent value="vente-logiciel" data-testid="tabcontent-vente-logiciel">
          <div className="bg-white rounded-lg -mx-6 -mb-6 px-2 py-2">
            <CRMVenteLogicielPage />
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Dialog Nouveau Contact */}
      <Dialog open={showNewContact} onOpenChange={setShowNewContact}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input 
                  className="bg-slate-700 border-slate-600"
                  value={newContact.prenom}
                  onChange={(e) => setNewContact(prev => ({ ...prev, prenom: e.target.value }))}
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input 
                  className="bg-slate-700 border-slate-600"
                  value={newContact.nom}
                  onChange={(e) => setNewContact(prev => ({ ...prev, nom: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input 
                type="email"
                className="bg-slate-700 border-slate-600"
                value={newContact.email}
                onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input 
                className="bg-slate-700 border-slate-600"
                value={newContact.telephone}
                onChange={(e) => setNewContact(prev => ({ ...prev, telephone: e.target.value }))}
              />
            </div>
            <div>
              <Label>Poste</Label>
              <Input 
                className="bg-slate-700 border-slate-600"
                value={newContact.poste}
                onChange={(e) => setNewContact(prev => ({ ...prev, poste: e.target.value }))}
              />
            </div>
            <div>
              <Label>Entreprise</Label>
              <Input 
                className="bg-slate-700 border-slate-600"
                value={newContact.entreprise_nom}
                onChange={(e) => setNewContact(prev => ({ ...prev, entreprise_nom: e.target.value }))}
              />
            </div>
            <div>
              <Label>Niveau d'intérêt</Label>
              <Select value={newContact.niveau_interet} onValueChange={(v) => setNewContact(prev => ({ ...prev, niveau_interet: v }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="froid">Froid</SelectItem>
                  <SelectItem value="tiede">Tiède</SelectItem>
                  <SelectItem value="chaud">Chaud</SelectItem>
                  <SelectItem value="tres_chaud">Très chaud</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreateContact} className="w-full">
              Créer le contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Nouvelle Entreprise */}
      <Dialog open={showNewEntreprise} onOpenChange={setShowNewEntreprise}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle Entreprise</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              <div>
                <Label>Nom *</Label>
                <Input 
                  className="bg-slate-700 border-slate-600"
                  value={newEntreprise.nom}
                  onChange={(e) => setNewEntreprise(prev => ({ ...prev, nom: e.target.value }))}
                />
              </div>
              <div>
                <Label>SIRET</Label>
                <Input 
                  className="bg-slate-700 border-slate-600"
                  value={newEntreprise.siret}
                  onChange={(e) => setNewEntreprise(prev => ({ ...prev, siret: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Secteur</Label>
                  <Select value={newEntreprise.secteur_activite} onValueChange={(v) => setNewEntreprise(prev => ({ ...prev, secteur_activite: v }))}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agriculture">Agriculture</SelectItem>
                      <SelectItem value="viticulture">Viticulture</SelectItem>
                      <SelectItem value="arboriculture">Arboriculture</SelectItem>
                      <SelectItem value="maraichage">Maraîchage</SelectItem>
                      <SelectItem value="elevage">Élevage</SelectItem>
                      <SelectItem value="commerce">Commerce</SelectItem>
                      <SelectItem value="services">Services</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={newEntreprise.type_client} onValueChange={(v) => setNewEntreprise(prev => ({ ...prev, type_client: v }))}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="prospect">Prospect</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="partenaire">Partenaire</SelectItem>
                      <SelectItem value="fournisseur">Fournisseur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Ville</Label>
                <Input 
                  className="bg-slate-700 border-slate-600"
                  value={newEntreprise.ville}
                  onChange={(e) => setNewEntreprise(prev => ({ ...prev, ville: e.target.value }))}
                />
              </div>
              <div>
                <Label>CA estimé (€)</Label>
                <Input 
                  type="number"
                  className="bg-slate-700 border-slate-600"
                  value={newEntreprise.ca_estime}
                  onChange={(e) => setNewEntreprise(prev => ({ ...prev, ca_estime: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <Button onClick={handleCreateEntreprise} className="w-full">
                Créer l'entreprise
              </Button>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Nouvelle Opportunité */}
      <Dialog open={showNewOpportunite} onOpenChange={setShowNewOpportunite}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle Opportunité</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom de l'opportunité *</Label>
              <Input 
                className="bg-slate-700 border-slate-600"
                placeholder="Ex: Contrat annuel fourniture..."
                value={newOpportunite.nom}
                onChange={(e) => setNewOpportunite(prev => ({ ...prev, nom: e.target.value }))}
              />
            </div>
            <div>
              <Label>Entreprise</Label>
              <Select 
                value={newOpportunite.entreprise_id} 
                onValueChange={(v) => {
                  const ent = entreprises.find(e => e.id === v);
                  setNewOpportunite(prev => ({ 
                    ...prev, 
                    entreprise_id: v,
                    entreprise_nom: ent?.nom || ''
                  }));
                }}
              >
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue placeholder="Choisir une entreprise" />
                </SelectTrigger>
                <SelectContent>
                  {entreprises.map(ent => (
                    <SelectItem key={ent.id} value={ent.id}>{ent.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant (€)</Label>
                <Input 
                  type="number"
                  className="bg-slate-700 border-slate-600"
                  value={newOpportunite.montant_estime}
                  onChange={(e) => setNewOpportunite(prev => ({ ...prev, montant_estime: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Probabilité (%)</Label>
                <Input 
                  type="number"
                  className="bg-slate-700 border-slate-600"
                  value={newOpportunite.probabilite}
                  onChange={(e) => setNewOpportunite(prev => ({ ...prev, probabilite: parseInt(e.target.value) || 10 }))}
                />
              </div>
            </div>
            <div>
              <Label>Étape</Label>
              <Select value={newOpportunite.etape} onValueChange={(v) => setNewOpportunite(prev => ({ ...prev, etape: v }))}>
                <SelectTrigger className="bg-slate-700 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospection">Prospection</SelectItem>
                  <SelectItem value="qualification">Qualification</SelectItem>
                  <SelectItem value="proposition">Proposition</SelectItem>
                  <SelectItem value="negociation">Négociation</SelectItem>
                  <SelectItem value="closing">Closing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreateOpportunite} className="w-full">
              Créer l'opportunité
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Analyse Marché IA */}
      <Dialog open={showAnalyseMarche} onOpenChange={setShowAnalyseMarche}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Analyse de Marché IA
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6">
            {/* Formulaire */}
            <div className="space-y-4">
              <div>
                <Label>Produit à analyser *</Label>
                <Input 
                  className="bg-slate-700 border-slate-600"
                  placeholder="Ex: patates douces, lavande, vin..."
                  value={analyseMarche.produit}
                  onChange={(e) => setAnalyseMarche(prev => ({ ...prev, produit: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Quantité</Label>
                  <Input 
                    type="number"
                    className="bg-slate-700 border-slate-600"
                    placeholder="Ex: 5000"
                    value={analyseMarche.quantite}
                    onChange={(e) => setAnalyseMarche(prev => ({ ...prev, quantite: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Unité</Label>
                  <Select value={analyseMarche.unite} onValueChange={(v) => setAnalyseMarche(prev => ({ ...prev, unite: v }))}>
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="L">Litres</SelectItem>
                      <SelectItem value="piece">Pièces</SelectItem>
                      <SelectItem value="tonne">Tonnes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Type de vente</Label>
                <Select value={analyseMarche.type_vente} onValueChange={(v) => setAnalyseMarche(prev => ({ ...prev, type_vente: v }))}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2B">B2B (Professionnels)</SelectItem>
                    <SelectItem value="B2C">B2C (Particuliers)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleAnalyseMarche} 
                disabled={!analyseMarche.produit || loadingAnalyse}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
              >
                {loadingAnalyse ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Analyser le marché
              </Button>
            </div>
            
            {/* Résultats */}
            <div>
              {analyseResult ? (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-700 rounded-lg">
                    <h4 className="font-semibold text-lg mb-2">{analyseResult.produit}</h4>
                    <p className="text-sm text-slate-400">Marché {analyseResult.type_vente} • {analyseResult.region}</p>
                  </div>
                  
                  <div className="p-4 bg-gradient-to-r from-green-900/50 to-teal-900/50 rounded-lg">
                    <h5 className="font-medium mb-2">Prix du marché</h5>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-2xl font-bold text-green-400">{analyseResult.prix_marche.min}€</p>
                        <p className="text-xs text-slate-400">Min</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-yellow-400">{analyseResult.prix_marche.moyen}€</p>
                        <p className="text-xs text-slate-400">Moyen</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-orange-400">{analyseResult.prix_marche.max}€</p>
                        <p className="text-xs text-slate-400">Max</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-purple-900/30 rounded-lg">
                    <h5 className="font-medium mb-2">Recommandations</h5>
                    <p className="text-purple-300">
                      Prix conseillé: <span className="font-bold">{analyseResult.recommandations.prix_min_recommande}€ - {analyseResult.recommandations.prix_max_recommande}€</span>
                    </p>
                    <p className="text-sm text-slate-400 mt-1">Marge estimée: {analyseResult.recommandations.marge_estimee}</p>
                  </div>
                  
                  {analyseResult.ca_potentiel && (
                    <div className="p-4 bg-yellow-900/30 rounded-lg">
                      <h5 className="font-medium mb-2">CA Potentiel</h5>
                      <p className="text-2xl font-bold text-yellow-400">
                        {analyseResult.ca_potentiel.ca_min.toLocaleString('fr-FR')}€ - {analyseResult.ca_potentiel.ca_max.toLocaleString('fr-FR')}€
                      </p>
                      <p className="text-xs text-slate-400">Pour {analyseResult.ca_potentiel.quantite} {analyseResult.ca_potentiel.unite}</p>
                    </div>
                  )}
                  
                  <div className="p-4 bg-slate-700 rounded-lg">
                    <h5 className="font-medium mb-2">Cibles clients</h5>
                    <div className="flex flex-wrap gap-2">
                      {analyseResult.recommandations.cible_clients.map((cible, i) => (
                        <Badge key={i} variant="outline">{cible}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  <div className="text-center">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Lancez une analyse pour voir les résultats</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Click-to-Call - Saisie post-appel */}
      <Dialog open={showCallModal} onOpenChange={setShowCallModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg" data-testid="call-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-green-500" />
              Appel en cours
            </DialogTitle>
          </DialogHeader>
          {activeCall && (
            <div className="space-y-4">
              {/* Info contact */}
              <div className="p-4 bg-slate-700 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center text-lg font-bold">
                    {activeCall.prenom?.[0]}{activeCall.nom?.[0]}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{activeCall.prenom} {activeCall.nom}</h3>
                    <p className="text-slate-400">{activeCall.entreprise_nom || 'Sans entreprise'}</p>
                    <p className="text-sm text-green-400">{activeCall.mobile || activeCall.telephone}</p>
                  </div>
                </div>
              </div>

              {/* Durée de l'appel */}
              {callStartTime && (
                <div className="text-center p-3 bg-green-900/30 rounded-lg">
                  <Timer className="w-5 h-5 inline mr-2" />
                  Appel démarré à {callStartTime.toLocaleTimeString('fr-FR')}
                </div>
              )}

              {/* Notes de l'appel */}
              <div>
                <Label>Notes de l'appel</Label>
                <Textarea 
                  className="bg-slate-700 border-slate-600 min-h-[120px]"
                  placeholder="Notez les points clés de la conversation... (l'IA analysera ces notes pour générer un résumé et des actions)"
                  value={callNotes}
                  onChange={(e) => setCallNotes(e.target.value)}
                  data-testid="call-notes-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  💡 Plus vos notes sont détaillées, meilleure sera l'analyse IA
                </p>
              </div>

              {/* Résultat de l'appel */}
              <div>
                <Label>Résultat</Label>
                <Select value={callResult} onValueChange={setCallResult}>
                  <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="call-result-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="positif">✅ Positif - Intéressé</SelectItem>
                    <SelectItem value="neutre">😐 Neutre - À suivre</SelectItem>
                    <SelectItem value="rappeler">📞 À rappeler</SelectItem>
                    <SelectItem value="negatif">❌ Négatif - Pas intéressé</SelectItem>
                    <SelectItem value="messagerie">📱 Messagerie</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowCallModal(false)}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={handleSaveCall}
                  disabled={savingCall}
                  data-testid="save-call-btn"
                >
                  {savingCall ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Terminer & Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fiche Entreprise Enrichie */}
      {selectedEntreprise && (
        <FicheEntreprise
          entreprise={selectedEntreprise}
          onClose={() => setSelectedEntreprise(null)}
          onUpdate={() => {
            fetchEntreprises();
            setSelectedEntreprise(null);
          }}
        />
      )}
    </div>
  );
}
